import React, { useContext, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Form } from 'antd'

import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'
import { NEXT_FORMALITY_STEP_MUTATION } from 'graphql/LegalFormalities'
import { FormalityFormulaire } from './styles'
import AuthorityRedropConfirm from './StepForm/AuthorityRedropConfirm'
import CapitalDepositConfirm from './StepForm/CapitalDepositConfirm'
import NextStepConfirm from './StepForm/NextStepConfirm'
import AuthorityRefuseConfirm from './StepForm/AuthorirtyRefuseConfirm'
import SubmissionFormConfirm from './StepForm/SubmissionFormConfirm'
import {
  closePopup,
  openErrorPopup,
  openPopup,
  openValidationPopup,
} from 'components/ConfirmationPopups'
import SignatureConfirm from './StepForm/SignatureConfirm'
import FormCategoryQuestions from './Panes/Form/FormCategoryQuestions'
import FormCompletionConfirm from './StepForm/FormCompletionConfirm'

export interface FormValuesProps {
  authorityRedrop?: boolean
  authorityRefusalReason?: string
  authorityRefuse?: boolean
  submissionId?: string
  metadatum?: string | JSON
  depositProvider?: string | null
}

const FormalityStepForm = () => {
  const { legalFormality, legalFormalityRefetch } = useContext(
    CurrentLegalFormalityContext
  )
  const [updateFormalityStep] = useMutation(NEXT_FORMALITY_STEP_MUTATION)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm<any>()
  const status = legalFormality.status
  const isAborted = status === 'flow_aborted'
  if (isAborted) {
    return <></>
  }

  const isJotform = legalFormality.formProvider === 'jotform'
  const hasForm = status !== 'formality_done'
  const hasCapitalDepositStep =
    legalFormality.steps.indexOf('capital_deposit_uploaded') !== -1

  const onFinish = (values: FormValuesProps) => {
    setSubmitting(true)
    const popupKey = openPopup(
      "Transition d'étape en cours",
      'merci de patienter.'
    )
    const submitValues = { ...values } as any
    submitValues.metadatum &&
      (submitValues.metadatum = JSON.stringify(submitValues.metadatum))
    updateFormalityStep({
      variables: {
        id: legalFormality.id,
        ...submitValues,
      },
    })
      .then((res: any) => {
        closePopup(popupKey)
        if (res.data.error) {
          const message = res.data.error.message
          openErrorPopup('Une erreur est survenue', message)
          throw message
        }
        openValidationPopup(
          "Transition d'étape terminée",
          'transition terminée'
        )
        res.data && legalFormalityRefetch()
      })
      .catch((e: any) => {
        console.log(e.message)
        closePopup(popupKey)
        openErrorPopup('Une erreur est survenue', e.message)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const nextConfirm = (
    <NextStepConfirm form={form} onFinish={onFinish} loading={submitting} />
  )

  const confirmBtns = () => {
    switch (status) {
      case 'form_sent':
        return isJotform ? (
          <SubmissionFormConfirm
            form={form}
            onFinish={onFinish}
            loading={submitting}
          />
        ) : (
          nextConfirm
        )
      case 'form_received':
        return (
          <FormCompletionConfirm
            form={form}
            onFinish={onFinish}
            loading={submitting}
          />
        )
      case 'document_generated':
        return !hasCapitalDepositStep ? (
          <SignatureConfirm
            form={form}
            onFinish={onFinish}
            loading={submitting}
          />
        ) : (
          nextConfirm
        )
      case 'capital_deposit_uploaded':
        return hasCapitalDepositStep ? (
          <CapitalDepositConfirm
            form={form}
            onFinish={onFinish}
            loading={submitting}
          />
        ) : (
          nextConfirm
        )
      case 'legal_authority_dropped':
        return (
          <>
            <AuthorityRefuseConfirm
              form={form}
              onFinish={onFinish}
              loading={submitting}
            />
            {nextConfirm}
          </>
        )
      case 'legal_authority_refused':
        return (
          <AuthorityRedropConfirm onFinish={onFinish} loading={submitting} />
        )
      default:
        return nextConfirm
    }
  }

  return (
    <FormalityFormulaire>
      {hasForm && (
        <Form
          form={form}
          name="nextFormalityStepForm"
          onFinish={() => onFinish}
          autoComplete="off"
          layout="vertical"
        >
          {confirmBtns()}
        </Form>
      )}
    </FormalityFormulaire>
  )
}

export default FormalityStepForm
